import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FilterEmptyState } from '@/components/ui/custom/FilterEmptyState';
import useBrains from '@/hooks/useBrains';
import { Brain } from '@/shared/types/brain';

import { FilterCheckboxItem } from './FilterCheckboxItem';
import { brainSearchTerms } from './filterSearchTerms';
import { SearchableCheckboxList } from './SearchableCheckboxList';
import { FilterPanelProps } from './types';

/**
 * Renders selectable brains (AI agents) for account-level filtering.
 * Mirrors the environment selector but sources data from the brains hook.
 */
export const BrainsFilter = ({
  filterIndex,
  searchText = '',
  onSearchChange = () => {},
}: FilterPanelProps) => {
  const { t } = useTranslation();
  const { brains } = useBrains();
  const { control, getValues } = useFormContext();

  const { update } = useFieldArray({ control, name: 'filters' });

  const brainIds = useWatch({ control, name: `filters.${filterIndex}.brainIds` }) || [];

  const toggleBrain = (brainId: string, checked: boolean) => {
    const currentBrainIds = Array.isArray(brainIds) ? [...brainIds] : [];
    const nextBrainIds = checked
      ? [...currentBrainIds, brainId]
      : currentBrainIds.filter((id: string) => id !== brainId);

    const currentFilter = getValues(`filters.${filterIndex}`);
    update(filterIndex, { ...currentFilter, brainIds: nextBrainIds });
  };

  if (!brains || brains.length === 0) {
    return <FilterEmptyState text={t('analytics.filters.no_ai_agents')} />;
  }

  return (
    <SearchableCheckboxList
      searchText={searchText}
      onSearchChange={onSearchChange}
      items={brains}
      getSearchTerms={(brain: Brain) => brainSearchTerms(brain)}
      placeholder={t('ai_agents.search_ai_agent')}
      renderItem={(brain: Brain) => (
        <FilterCheckboxItem
          key={brain.brain_id}
          id={`brain-${brain.brain_id}`}
          checked={brainIds.includes(brain.brain_id)}
          onCheckedChange={(checked) => toggleBrain(brain.brain_id, checked as boolean)}
        >
          {brain.name}
        </FilterCheckboxItem>
      )}
    />
  );
};
